import { AxiosInstance } from 'axios';
import { result, Result } from 'util/result';
import {
  AppResponse,
  AppsResponse,
} from 'data/terra/api/terra/response/AppResponse';
import Terra from '../Terra';
import {
  AppVersionResponse,
  AppVersionsResponse,
} from 'data/terra/api/terra/response/AppVersionResponse';
import { CreateAppRequest } from 'data/terra/App';
import { AppVersion } from 'data/terra/AppVersion';

class AppEndpoint {
  getAppsUrl = () => `${Terra.TERRA_HOST}/${Terra.TERRA_BASE_API_URL}/apps`;
  getAppDetailUrl = (appId: string) =>
    `${Terra.TERRA_HOST}/${Terra.TERRA_BASE_API_URL}/apps/${appId}`;
  getAppVersionsUrl = (appId: string) =>
    `${Terra.TERRA_HOST}/${Terra.TERRA_BASE_API_URL}/apps/${appId}/versions`;
  getAppVersionDetailUrl = (appId: string, versionId: string) =>
    `${Terra.TERRA_HOST}/${Terra.TERRA_BASE_API_URL}/apps/${appId}/versions/${versionId}`;
}

export class AppApi {
  terraClient: AxiosInstance;
  endpoint = new AppEndpoint();

  constructor(client: AxiosInstance) {
    this.terraClient = client;
  }

  fetchApps(productId: string): Promise<Result<AppsResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.get<AppsResponse>(this.endpoint.getAppsUrl(), {
            params: {
              productId,
              pageSize: 50,
            },
          })
        ).data
    );
  }

  addApp(data: CreateAppRequest): Promise<Result<AppResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.post<AppResponse>(
            this.endpoint.getAppsUrl(),
            data
          )
        ).data
    );
  }

  fetchAppVersions(appId: string): Promise<Result<AppVersionsResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.get<AppVersionsResponse>(
            this.endpoint.getAppVersionsUrl(appId),
            {
              params: { sortBy: '-created_at', pageSize: 50 },
            }
          )
        ).data
    );
  }

  createAppVersion(
    appId: string,
    code: string,
    note?: string,
    baseVersionId?: string,
    activeEnvironmentId?: string
  ): Promise<Result<AppVersionResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.post<AppVersionResponse>(
            this.endpoint.getAppVersionsUrl(appId),
            {
              code,
              note,
              baseVersionId,
              activeEnvironmentId
            }
          )
        ).data
    );
  }

  updateAppVersion(
    appId: string,
    appVersion: AppVersion,
    note: string
  ): Promise<Result<AppVersionResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.put<AppVersionResponse>(
            this.endpoint.getAppVersionDetailUrl(appId, appVersion.id),
            {
              ...appVersion,
              note,
            }
          )
        ).data
    );
  }

  deleteAppVersion(
    appId: string,
    appVersion: AppVersion
  ): Promise<Result<boolean>> {
    return result(async () => {
      await this.terraClient.delete<void>(
        this.endpoint.getAppVersionDetailUrl(appId, appVersion.id)
      );
      return true;
    });
  }
}
